import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FormState, FieldDefinition, ValidationRule } from './edit-modal/types';
import { findCardInSequence } from './utils';
import { validateForm } from './edit-modal/validation';
import { fieldRegistry } from './edit-modal/fieldRegistry';
import './edit-modal/registerFields';
import { useEditorStore } from '~/shell/Store';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';
import { Overlay } from '~/ui/overlay/Overlay';
import {
  ModalWrapper,
  ModalContent,
  ModalHeader,
  Title,
  CloseButton,
  Form,
  ButtonRow,
  SubmitButton
} from './edit-modal/sharedComponents';
import { Modal } from '~/ui/modal/Modal';
import CloseIcon from '~/assets/icons/close.svg';
import { GLOBAL_PLACEHOLDER } from './templates/constants';

interface CardModalProps {
  mode: 'create' | 'edit';
  open: boolean;
  onClose: () => void;
  cardId?: string | number;
  fields: FieldDefinition[];
  validation?: ValidationRule;
  modalTitle: string;
  onCreate?: (data: Record<string, unknown>) => void;
}

export const CardModal: React.FC<CardModalProps> = ({
  mode,
  open,
  onClose,
  cardId,
  fields,
  validation,
  modalTitle: modalTitleProp,
  onCreate
}) => {
  const {
    sequence,
    updateSubItemBatch,
    updateFile,
    editorMode,
    setForceUpdateSequence,
    globalPlaceholders
  } = useEditorStore();

  const cardItem = useMemo(() => {
    if (mode === 'create') return null;
    if (!cardId) return null;
    return findCardInSequence(sequence!, cardId);
  }, [sequence, cardId, mode]);

  const [formState, setFormState] = useState<FormState>({});

  const hasInitialized = useRef(false);

  const effectiveConfig = useMemo(() => {
    return {
      fields,
      validation,
      modalTitle: modalTitleProp
    };
  }, [fields, validation, modalTitleProp]);

  useEffect(() => {
    if (!open) {
      hasInitialized.current = false;
      return;
    }

    if (hasInitialized.current) return;

    const initialState: FormState = effectiveConfig.fields.reduce(
      (state, field) => {
        let value: unknown;

        if (mode === 'create') {
          let defaultValue = field.defaultValue ?? null;

          if (defaultValue === GLOBAL_PLACEHOLDER) {
            const resolver = fieldRegistry.getPlaceholderResolver(field.type);
            defaultValue = (
              resolver
                ? resolver(
                    field,
                    globalPlaceholders as unknown as Record<string, unknown>
                  )
                : null
            ) as typeof defaultValue;
          }

          value = defaultValue;
        } else {
          if (!cardItem) return state;

          value = fieldRegistry.extractValue(field, cardItem);
        }

        return { ...state, [field.name]: value as FormState[string] };
      },
      {} as FormState
    );

    setFormState(initialState);
    hasInitialized.current = true;
  }, [mode, cardItem, open, effectiveConfig, globalPlaceholders]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const modalTitle = modalTitleProp || effectiveConfig.modalTitle;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm(effectiveConfig, formState);
    if (validationError) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: validationError
      });
      return;
    }

    if (mode === 'create') {
      if (!onCreate) return;

      const data: Record<string, unknown> = {};

      effectiveConfig.fields.forEach(field => {
        const currentValue = formState[field.name];
        const serializer = fieldRegistry.getApiSerializer(field.type);
        if (serializer) {
          serializer(field, currentValue, data);
        }
      });

      onCreate(data);

      onClose();
      return;
    }

    if (!cardId) return;

    const updates: Record<string, unknown> = {};

    effectiveConfig.fields.forEach(field => {
      const currentValue = formState[field.name];
      const updateHandler = fieldRegistry.getUpdateHandler(field.type);

      if (updateHandler) {
        updateHandler(
          field,
          currentValue,
          cardItem as Record<string, unknown>,
          String(cardId),
          {
            updateFile,
            addToBatch: (fieldName, value) => {
              updates[fieldName] = value;
            }
          }
        );
      }
    });

    if (Object.keys(updates).length > 0) {
      const result = await updateSubItemBatch(cardId, updates);
      if (result?.error) {
        return;
      }
    }

    setForceUpdateSequence(true);
    onClose();
  };

  if (!open || !editorMode) {
    return null;
  }

  const modalContent = (
    <Overlay open={true} opacity={0.5} onClick={onClose}>
      <ModalWrapper
        onClick={e => e.stopPropagation()}
        onPointerDown={e => e.stopPropagation()}
      >
        <Modal
          open={true}
          width="462px"
          horizontal="center"
          vertical="center"
          borderRadius="20px"
        >
          <ModalContent>
            <ModalHeader>
              <Title>{modalTitle}</Title>
              <CloseButton onClick={onClose} aria-label="Close card modal">
                <CloseIcon />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              {effectiveConfig.fields.map(field => {
                const contextProvider = fieldRegistry.getContextProvider(
                  field.type
                );
                const context = contextProvider
                  ? contextProvider(field, cardItem as Record<string, unknown>)
                  : {};

                return fieldRegistry.render(field, formState, setFormState, {
                  ...context,
                  cardId
                });
              })}

              <ButtonRow>
                <SubmitButton type="submit">Submit</SubmitButton>
              </ButtonRow>
            </Form>
          </ModalContent>
        </Modal>
      </ModalWrapper>
    </Overlay>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
